import React, { FC, useMemo } from 'react';
import {
  Box,
  Center,
  Flex,
  HStack,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Spinner,
  Text,
  VStack
} from '@chakra-ui/react';
import { useAddress, useContract } from '@thirdweb-dev/react';
import { NonfungiblePositionManager } from '@cryptoalgebra/kim-sdk';
import { algebraPositionManagerABI } from '../../../../../abis/v3';
import { useAsyncFn } from 'react-use';
import { SubmitButton } from '@app/components/SubmitButton';
import { showErrorToast, showSuccessToast } from '@app/components/Toast';
import { ExternalLink } from '@app/components/ExternalLink';
import { usePositionFees } from '@app/hooks/v3/usePositionFees';
import { IDerivedMintInfo } from '@app/state/mintStore';
import { formatUSD } from '@app/helpers/formatUSD';
import { useConfig } from '@app/config';
import { TokenIcon } from '@app/components/TokenIcon';
import { QueryKeys } from '@app/constants/queryKeys';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  onClose: () => void;
  isOpen: boolean;
  mintInfo: IDerivedMintInfo;
  positionFeesUSD: number;
  positionId: number;
}

export const CollectFeesModalV3Content: FC<Props> = ({
  mintInfo,
  positionFeesUSD,
  positionId,
  isOpen,
  onClose
}) => {
  const queryClient = useQueryClient();
  const account = useAddress();
  const pool = mintInfo.pool;
  const { amount0, amount1 } = usePositionFees(
    pool ?? undefined,
    positionId,
    false
  );

  const zeroRewards = amount0?.equalTo('0') && amount1?.equalTo('0');

  const { calldata, value } = useMemo(() => {
    if (!account || !amount0 || !amount1)
      return { calldata: undefined, value: undefined };

    return NonfungiblePositionManager.collectCallParameters({
      tokenId: positionId.toString(),
      expectedCurrencyOwed0: amount0,
      expectedCurrencyOwed1: amount1,
      recipient: account
    });
  }, [positionId, amount0, amount1, account]);

  const config = useConfig();

  const { contract } = useContract(
    config?.ALGEBRA_ADDRESSES.ALGEBRA_POSITION_MANAGER,
    algebraPositionManagerABI
  );

  const [{ loading: isLoading }, collect] = useAsyncFn(async () => {
    if (!contract || !calldata) {
      return;
    }

    try {
      const res = await contract.call('multicall', [calldata], {
        value: BigInt(value || 0)
      });

      if (res?.reason) {
        showErrorToast('Transaction rejected');
      }

      const txHash = res?.receipt?.transactionHash;

      if (txHash) {
        showSuccessToast(
          `Transaction successful.`,
          <ExternalLink txHash={txHash} />
        );

        await queryClient.invalidateQueries([QueryKeys.POOL_FEE_DATA]);
        await queryClient.invalidateQueries([QueryKeys.POSITION_FEES]);

        onClose();
      }
    } catch (e: any) {
      console.error(e.message);

      if (e.message?.includes('user rejected transaction')) {
        return;
      }

      showErrorToast(`Transaction failed: ${e.message}`);
    }
  }, [contract, calldata, queryClient]);

  const collectedFees =
    formatUSD.format(positionFeesUSD) === '$0' && !zeroRewards
      ? '< $0.001'
      : formatUSD.format(positionFeesUSD);

  const symbol0 =
    amount0?.currency.symbol === 'WETH' ? 'ETH' : amount0?.currency.symbol;
  const symbol1 =
    amount1?.currency.symbol === 'WETH' ? 'ETH' : amount1?.currency.symbol;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      motionPreset="slideInBottom"
      size="xs"
    >
      <ModalOverlay />
      <ModalContent sx={{ maxWidth: ['100vw', '600px'] }}>
        <ModalHeader>
          <HStack justify="space-between" px={0} py={0} mb="16px">
            <Text fontSize="16px" color="white" fontWeight="500">
              Earned fees
            </Text>
            <ModalCloseButton onClick={onClose} />
          </HStack>
        </ModalHeader>
        <VStack w="100%">
          {!amount0 && !amount0 ? (
            <Flex w="100%" h="100%" minHeight="84px" justifyContent="center">
              <Center>
                <Spinner color="orange" width={8} height={8} />
              </Center>
            </Flex>
          ) : (
            <>
              <HStack w="100%" py={2}>
                {amount0 ? (
                  <>
                    <TokenIcon symbol={symbol0} />
                    <Text fontSize="14px" color="white" fontWeight="500">
                      {amount0.toSignificant()}
                    </Text>
                    <Text fontSize="14px" color="neutral.300" fontWeight="500">
                      {symbol0}
                    </Text>
                  </>
                ) : (
                  <Skeleton className="w-[100px] h-[30px]" />
                )}
              </HStack>
              <HStack w="100%" py={2}>
                {amount1 ? (
                  <>
                    <TokenIcon symbol={symbol1} />
                    <Text fontSize="14px" color="white" fontWeight="500">
                      {amount1.toSignificant()}
                    </Text>
                    <Text fontSize="14px" color="neutral.300" fontWeight="500">
                      {symbol1}
                    </Text>
                  </>
                ) : (
                  <Skeleton className="w-[100px] h-[30px]" />
                )}
              </HStack>
            </>
          )}

          <Box w="100%" py={4} display="none">
            {collectedFees ? (
              <Text fontSize="18px" color="gray.400" fontWeight="500">
                {collectedFees}
              </Text>
            ) : (
              <Skeleton className="w-[100px] h-[30px]" />
            )}
          </Box>

          <SubmitButton
            label="Collect fees"
            isLoading={isLoading}
            disabled={!collect || zeroRewards || isLoading}
            onClick={() => collect && collect()}
          />
        </VStack>
      </ModalContent>
    </Modal>
  );
};
